/**
 * Which of a reader's own days an instant falls in.
 *
 * A daily limit needs a day, and a day is not the same thing for everybody. A
 * reader in Shanghai finishing a session at one in the morning is still on the
 * day they started, and a reader who moves country expects their day to move
 * with them. So the boundary is theirs to set. Anki's rollover setting is the
 * model.
 *
 * **A due date takes no notice of any of this.** Cards are scheduled as
 * instants and the queue asks whether the instant has passed. What the boundary
 * decides is the daily limits and the count kept under them.
 *
 * `Intl.DateTimeFormat` carries the time zone database, in Node and in a
 * browser, with nothing to install. That is what lets this module be the one
 * answer on both sides of the wire.
 */

import { formatterFor, partOf } from "./zone-formatter.js";

export { instantOfLocalHour } from "./local-hour.js";

/** When a reader's day begins. */
export interface DayBoundary {
  /**
   * The IANA name of the zone the reader keeps, such as `Asia/Shanghai`.
   *
   * A name rather than an offset, so a reader who sets this once keeps the
   * right day through their own summer time.
   */
  readonly timeZone: string;

  /**
   * The hour their day begins, from 0 to 23.
   *
   * Four in the morning by default, which is Anki's. A session that runs past
   * midnight belongs to the day it started on, and somebody studying at two is
   * far commoner than somebody studying at four.
   */
  readonly dayStartsAt: number;
}

/** The boundary a reader gets before they have chosen one. */
export const defaultDayBoundary: DayBoundary = {
  timeZone: "UTC",
  dayStartsAt: 4,
};

/** Milliseconds in a day, for stepping a calendar date back over the boundary. */
const aDay = 86_400_000;

/**
 * Whether a string names a time zone this runtime knows.
 *
 * The check a settings form makes. `Intl.DateTimeFormat` throws a `RangeError`
 * for a name it cannot resolve, and a stored zone that throws would fail every
 * answer a reader gave from then on.
 *
 * @param given - Whatever arrived, which is a form field or a stored value.
 * @returns Whether a formatter can be built for it.
 */
export function isTimeZone(given: string): boolean {
  try {
    formatterFor(given);

    return true;
  } catch {
    return false;
  }
}

/**
 * Whether a number names an hour a day can begin at.
 *
 * @param given - Whatever arrived.
 * @returns Whether it is a whole number of hours from 0 to 23.
 */
export function isDayStart(given: number): boolean {
  return Number.isInteger(given) && given >= 0 && given <= 23;
}

/** A wall clock reading, as the reader's own zone shows it. */
interface WallClock {
  /** The calendar date, as `YYYY-MM-DD`. */
  readonly date: string;

  /** The hour of that date, from 0 to 23. */
  readonly hour: number;
}

/** What the clock on the reader's wall says at an instant. */
function wallClockAt(at: Date, timeZone: string): WallClock {
  const parts = formatterFor(timeZone).formatToParts(at);

  return {
    date: `${partOf(parts, "year")}-${partOf(parts, "month")}-${partOf(parts, "day")}`,
    hour: Number(partOf(parts, "hour")),
  };
}

/**
 * The calendar date a number of days before this one.
 *
 * Arithmetic on a date and never on an instant. The date is anchored in UTC for
 * the subtraction, so it steps back exactly that many days whatever the
 * reader's zone was doing, and comes back out as the same kind of label it went
 * in as.
 *
 * Exported because 回顾 walks a window of a reader's own days back from today
 * and reads the counters filed under each. Their labels were written by
 * {@link reviewDayOf}, so the stepping has to be the same stepping.
 * @param date - A day label, as `YYYY-MM-DD`.
 * @param days - How many days to go back. One gives the day before.
 * @returns The earlier label.
 */
export function dayLabelBack(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00.000Z`) - days * aDay)
    .toISOString()
    .slice(0, 10);
}

/**
 * The day an instant falls in, for the reader whose boundary this is.
 *
 * The instant is resolved into the reader's own zone first, and the calendar
 * date is stepped back only when the local hour has yet to reach the boundary.
 * That puts the small hours of a night onto the day before.
 *
 * **Shifting the instant by the boundary hour and then resolving the zone is
 * the version that looks simpler and is wrong.** The two are the same answer
 * only while the zone's offset holds still across the shift. On the morning
 * clocks go back in London, half past three in the morning is four hours after
 * half past ten the previous evening by the wall clock and five hours by the
 * instant, so a shift of four lands the reader on the wrong side of their own
 * boundary. `review-day.iso.test.ts` fails on exactly that instant.
 *
 * @param at - The instant, which is when an answer was accepted.
 * @param boundary - The reader's zone and the hour their day begins.
 * @returns The day, as `YYYY-MM-DD`.
 */
export function reviewDayOf(at: Date, boundary: DayBoundary): string {
  const wall = wallClockAt(at, boundary.timeZone);

  return wall.hour < boundary.dayStartsAt
    ? dayLabelBack(wall.date, 1)
    : wall.date;
}
